import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { Option } from "../../../common/types/Option";
import { svodPlanFilterOptions, allIncomeClassificators, allExpenseClassificators } from "../report-svod-plan";
import { SvodPlanFilterSettingsForm } from "../settings-form/SvodPlanFilterSettingsForm";
import { SvodPlanFilterSettings } from "../settings/SvodPlanFilterSettings";
import { ReportSvodPlanTableDataRequest } from "../ReportSvodPlanTableDataRequest";
import { BudgetType, DevelopType, MutuallyRedeemingLevel, Segmentation } from '../../../common/types/roots/fields';
import {Draggable} from "@/modules/budget/monitoring/reports-constructor/common/types/Draggable";

export class SvodPlanFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(svodPlanFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'SVOD-PLAN') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'SVOD-PLAN') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        const regionOption: Option<string> | null = cloneDeep(options.regions.find(it => it.value === settings.region) ?? null)

        const guOption: Option<string> | null = cloneDeep(options.gu.find(it => it.value === settings.gu) ?? null)

        const guOption261: Option<string> | null = cloneDeep(options.gu261.find(it => it.value === settings.gu261) ?? null)

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let svodType = options.svodTypes.find(it => it.value === settings.svodType)
        if (!svodType) {
            throw new Error('error_not_found_svod_type')
        }
        svodType = { ...svodType }

        let planType = options.planTypes.find(it => it.value === settings.planType)
        if (!planType) {
            throw new Error('error_not_found_svod_type')
        }
        planType = { ...planType }

        let typePF = options.typePFs.find(it => it.value === settings.typePF)
        if (!typePF) {
            throw new Error('error_not_found_svod_type')
        }
        typePF = { ...typePF }

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption } 

        let segmentationOptions = options.segmentations
        if (!segmentationOptions) {
            throw new Error('error_not_found_segmentation_option')
        }
        segmentationOptions = [ ...segmentationOptions ]

        // Разрезность из шаблона: порядок и активность — сохранённые, подписи — из опций.
        // Шаблоны без сохранённой разрезности открываются как раньше (Регион + КБК)
        const savedSegmentations = (settings.segmentations ?? [])
            .filter(saved => segmentationOptions.some(option => option.value === saved.value))
            .map(saved => ({ ...segmentationOptions.find(option => option.value === saved.value)!, active: saved.active }))
        const allSegmentationOptions: Draggable<Segmentation>[] = savedSegmentations.length > 0
            ? savedSegmentations
            : [
                { value: 'REGION', label: "Регион", active: true },
                { value: 'KBK', label: "КБК", active: true }
            ]

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        const statuses = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        const budgetTypes: Option<BudgetType>[] = []
        settings.budgetTypes.forEach(budget => { // TODO: just filter
            const budgetTypeOption = options.budgetTypes.find(item => item.value === budget)!
            budgetTypes.push({...budgetTypeOption})
        })

        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        const mutuallyRedeemingLevelOptions: Option<MutuallyRedeemingLevel>[] = []
        settings.mutuallyRedeemingLevels.forEach(level => {
            const option = options.mutuallyRedeemingLevels.find(it => it.value === level)!
            mutuallyRedeemingLevelOptions.push({...option})
        })

        const paymentTypeOption = cloneDeep(
            options.typePFComponent.find(it => it.value === settings.typePFComponent) ?? null
        )


        // 2. Формирование
        const form: SvodPlanFilterSettingsForm = {
            // Base:
            key: 'SVOD-PLAN',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            measureUnit: measureUnitOption,
            mutuallyRedeeming: mutuallyRedeemingOption,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo ?? null,
            // Custom:
            svodType: svodType, 
            planType: planType, 
            typePF: typePF,
            typePFComponent: paymentTypeOption,
            year: settings.year,
            region: regionOption,
            gu: guOption,
            gu261: guOption261,
            date: new Date(settings.date),
            statuses: statuses,
            incomes: cloneDeep(settings.incomes),
            expenses: cloneDeep(settings.expenses),
            budgetTypes: budgetTypes,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            calcCurPeriod: false,
            showTransferts: settings.showTransferts ?? false,
            disableRemainCurPeriod: settings.disableRemainCurPeriod ?? false,
            segmentations: allSegmentationOptions,
            developType: options.developTypes.find(it => it.value === (settings.developType ?? null)) ?? options.developTypes.find(it => it.value === null)!,
            msuAggregation: settings.msuAggregation ?? false
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'SVOD-PLAN') {
            throw new Error('error_type_filter_settingsForm')
        }
        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (form.region == null) {
            throw new Error('error_input_region')
        }
        if (form.planType.value === 'PF' && form.typePF.value === 'IPF' && form.gu === null) {
            throw new Error('error_select_gu')
        }
        if (form.year == null) {
            throw new Error('error_input_year')
        }
        if (form.periodicity.value === 'DAY' && !form.date) {
            throw new Error('error_input_date')
        }
        if (form.budgetTypes.length === 0) {
            throw new Error('error_select_budget')
        }

        if (!form.segmentations) {
            throw new Error('error_input_segmentation')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        let tempGu = null
        if (form.gu) {
            tempGu = form.gu.value
        }

        let tempGu261 = null
        if (form.gu261) {
            tempGu261 = form.gu261.value
        }
        
        const strDate = moment(form.date).format('YYYY-MM-DD')

        const settings: SvodPlanFilterSettings = {
            // Base:
            reportName: 'SVOD-PLAN',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            budgetTypes: form.budgetTypes.map(it => it.value),
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            // Custom:
            svodType: form.svodType.value,
            planType: form.planType.value,
            year: form.year,
            region: form.region.value,
            date: strDate,
            statuses: form.statuses.map(status => status.value),
            incomes: cloneDeep(form.incomes),
            expenses: cloneDeep(form.expenses),
            gu: tempGu,
            gu261: tempGu261,
            typePF: form.typePF.value,
            typePFComponent: form.typePFComponent?.value ?? null,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels?.map(it => it.value) ?? [],
            calcCurPeriod: false,
            showTransferts: form.showTransferts,
            disableRemainCurPeriod: form.disableRemainCurPeriod,
            segmentations: form.segmentations,
            developType: form.developType?.value ?? null,
            msuAggregation: form.msuAggregation ?? false
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(svodPlanFilterOptions)

        const emptyForm: SvodPlanFilterSettingsForm = {
            key: 'SVOD-PLAN',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'SVOD-PLAN')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'MIXED')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'YEAR')!,
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITHOUT')!,
            roundUp: false,
            roundUpTo: null,
            budgetTypes: [],
            mutuallyRedeemingLevels: [],
            // Custom fields
            svodType: options.svodTypes.find(it => it.value === 'APPROVED')!,
            planType: options.planTypes.find(it => it.value === 'PF')!,
            typePF: options.typePFs.find(it => it.value === 'IPF')!,
            typePFComponent: options.typePFComponent.find(it => it.value === 'PAYMENTS')!,
            year: (new Date().getFullYear()),
            region: null,
            gu: null,
            gu261: null,
            date: null,
            statuses: [options.statuses.find(it => it.value === '39')!],
            incomes: cloneDeep(allIncomeClassificators),
            expenses: cloneDeep(allExpenseClassificators),
            calcCurPeriod: false,
            showTransferts: false,
            disableRemainCurPeriod: false,
            segmentations: [],
            developType: options.developTypes.find(it => it.value === null)!,
            msuAggregation: false
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'SVOD-PLAN') {
            throw new Error('error_type_filter_settingsForm')
        }
        let date: any = `${form.year}-01-01`
        if (form.periodicity.value === 'DAY') {
            date = form.date
        }
        if (form.periodicity.value === 'DAY' && !form.date) {
            throw new Error('error_input_date')
        }
        
        if (!form.region) {
            throw new Error('error_input_region')
        }

        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }

        if (form.budgetTypes.length === 0) {
            throw new Error('error_select_budget')
        }

        if (form.planType.value === 'PF' && form.typePF.value === 'IPF' && form.gu === null) {
            throw new Error('error_select_gu')
        }

        if ((!form.segmentations?.filter(it => it.active).length) && !(form.typePF.value === 'IPF' && form.planType.value === 'PF')) {
            throw new Error('error_input_segmentation')
        }

        const cutness: string[] | undefined = form.segmentations?.filter(v => v.active).map(v => v.value)
        if (cutness == null) {
            throw new Error('error_unsupported_segmentation_type')
        }

        const strDate = moment(date).format('YYYY-MM-DD')

        const params = this.constructDataRequest(form, userId)

        const response = await fetch('/api-py/monitoring/reports-constructor/svod-plan',
            {
                method: 'POST',
                cache: 'no-cache',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseData: any = await response.json()
        const tableData: any = responseData

        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: any = {
            reportName: "SVOD-PLAN",

            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,

            region: form.region.value,
            report_date: strDate,
            measure_unit: form.measureUnit.value,
            budget_types: form.budgetTypes.map(it => it.value),
            rows: tableData,
            svod_type: form.svodType.value,
            type_pf: form.typePF.value,
            type_pf_component: form.typePFComponent?.value,
            expenses: form.expenses,
            incomes: form.incomes,
            gu: form.gu?.label,
            gu261: form.gu261?.label,
            periodicity: form.periodicity.value,
            plan_type: form.planType.value,
            report: '',
            calcCurPeriod: form.calcCurPeriod,
            cutness: cutness
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }

    public constructDataRequest(form: FilterSettingsForm, userId: string): ReportSvodPlanTableDataRequest {
        if (form.key !== 'SVOD-PLAN') {
            throw new Error('error_type_filter_settingsForm')
        }

        let date: any = `${form.year}-01-01`
        if (form.periodicity.value === 'DAY') {
            date = form.date
        }
        if (form.periodicity.value === 'DAY' && !form.date) {
            throw new Error('error_input_date')
        }
        
        if (!form.region) {
            throw new Error('error_input_region')
        }

        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }

        if (form.budgetTypes.length === 0) {
            throw new Error('error_select_budget')
        }

        if (form.planType.value === 'PF' && form.typePF.value === 'IPF' && form.gu === null) {
            throw new Error('error_select_gu')
        }

        if ((!form.segmentations?.filter(it => it.active).length) && !(form.typePF.value === 'IPF' && form.planType.value === 'PF')) {
            throw new Error('error_input_segmentation')
        }

        const cutness: string[] | undefined = form.segmentations?.filter(v => v.active).map(v => v.value)
        if (cutness == null) {
            throw new Error('error_unsupported_segmentation_type')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        let tempGu: Option<string> | null = null
        if (form.gu) {
            tempGu = form.gu ?? null
        }

        let tempGu261: Option<string> | null = null
        if (form.gu261) {
            tempGu261 = form.gu261 ?? null
        }

        const tempIncomes = form.incomes
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))

        const tempExpenses = form.expenses
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))

        const strDate = moment(date).format('YYYY-MM-DD')

        const settings: FilterSettings = this.formToSettings(cloneDeep(form))

        let year = form.year
        if (form.periodicity.value === 'DAY' && form.date) {
            year = moment(date).year()
            if (settings.reportName === 'SVOD-PLAN') {
                settings.year = year
            }
        }

        const params: ReportSvodPlanTableDataRequest = {
            user_id: userId,
            incomes: tempIncomes,
            expenses: tempExpenses,
            date: strDate,
            region: form.region.value,
            vzk: form.mutuallyRedeeming?.value,
            measureUnit: form.measureUnit.value,
            roundTo: roundUpTo,
            statuses: form.statuses.map(status => status.value),
            budget_types: form.budgetTypes.map(it => it.value),
            mutually_redeeming_levels: form.mutuallyRedeemingLevels.map(it => it.value),
            dataSource: form.dataSource.value,
            svod_type: form.svodType.value,
            plan_type: form.planType.value,
            typePF: form.typePF.value,
            typePFComponent: form.typePFComponent?.value ?? null,
            year: year,
            gu: tempGu?.value ?? null,
            gu261: tempGu261?.value ?? null,
            calcCurPeriod: form.calcCurPeriod,
            showTransferts: form.showTransferts,
            disableRemainCurPeriod: form.disableRemainCurPeriod,
            cutness: cutness,
            lang: i18nService.locale,
            filter_settings: settings,
            develop_type: form.developType?.value ?? null,
            msu_aggregation: form.msuAggregation ?? false
        }

        return params
    }
}